"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import content from "@/content/content.json";
import { track } from "@/lib/analytics";

function ThemeToggle({
  theme,
  label,
  onToggle,
}: {
  theme: "dark" | "light";
  label: string;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      aria-label={label}
      className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] text-[var(--muted)] hover:text-[var(--text)] transition-colors"
    >
      {theme === "dark" ? (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z"
          />
        </svg>
      ) : (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
          />
        </svg>
      )}
    </button>
  );
}

export default function Nav() {
  const t = useTranslations("nav");
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  // Read from the DOM attribute already set by the anti-FOUC inline script,
  // which resolved localStorage → OS preference → dark. This avoids any
  // icon flicker on first render for light-OS users.
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    if (typeof document === "undefined") return "dark";
    return document.documentElement.getAttribute("data-theme") === "light"
      ? "light"
      : "dark";
  });

  const menuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the mobile menu on Escape and return focus to the toggle button.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  function toggleTheme() {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    if (next === "light") {
      document.documentElement.setAttribute("data-theme", "light");
    } else {
      document.documentElement.removeAttribute("data-theme");
    }
    localStorage.setItem("theme", next);
    track("theme_toggle", { theme: next });
  }

  const navLinks = content.nav.links.map((link) => ({
    href: link.href,
    label: t(link.key as Parameters<typeof t>[0]),
  }));

  const themeToggleLabel = theme === "dark" ? t("lightMode") : t("darkMode");

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[var(--bg)]/90 backdrop-blur-md border-b border-white/5" : ""
      }`}
    >
      <nav className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link
          href="/"
          className="font-display text-[var(--text)] tracking-tight hover:text-[var(--accent-text)] transition-colors"
        >
          <span className="font-light">{content.nav.ownerName.first}</span>{" "}
          <span className="font-bold">{content.nav.ownerName.last}</span>
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-3">
          <ul className="flex items-center gap-8">
            {navLinks.map((l) => (
              <li key={l.href}>
                <Link
                  href={l.href}
                  onClick={() => track("navigation_click", { link_text: l.label, destination: l.href })}
                  className="font-mono text-xs uppercase tracking-widest text-[var(--muted)] hover:text-[var(--accent-text)] transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
          <ThemeToggle theme={theme} label={themeToggleLabel} onToggle={toggleTheme} />
        </div>

        {/* Mobile controls */}
        <div className="flex items-center gap-2 md:hidden">
          <ThemeToggle theme={theme} label={themeToggleLabel} onToggle={toggleTheme} />
          <button
            ref={menuButtonRef}
            className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] text-[var(--muted)] hover:text-[var(--text)] transition-colors"
            onClick={() => {
              const next = !open;
              setOpen(next);
              track(next ? "mobile_menu_open" : "mobile_menu_close");
            }}
            aria-label={t("toggleMenu")}
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {open ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div id="mobile-menu" className="md:hidden bg-[var(--surface)] border-b border-white/5 px-6 py-4">
          <ul className="flex flex-col gap-4">
            {navLinks.map((l) => (
              <li key={l.href}>
                <Link
                  href={l.href}
                  className="block py-2.5 font-mono text-sm uppercase tracking-widest text-[var(--muted)] hover:text-[var(--accent-text)] transition-colors"
                  onClick={() => {
                    setOpen(false);
                    track("navigation_click", { link_text: l.label, destination: l.href });
                  }}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
}
